import { coletarCiclosEmpresa, coletarEstadosFormulas, registrarCicloEmpresa } from '@shared/components/formula/utils/formula-estados.util';
import { FormulaCicloDialogComponent, FormulaCicloDialogData, FormulaCicloDialogResult } from '@shared/components/formula/formula-ciclo-dialog/formula-ciclo-dialog.component';
import { FormulaErrorCodeEnum } from '@shared/enums/formula/formula-error-code.enum';
import { TipoEntidadeFormulaEnum } from '@shared/enums/formula/tipo-entidade-formula.enum';
import { FormulaCicloEmpresaRascunho } from '@shared/services/structs/formula/formula-ciclo-empresa-rascunho.struct';
import { DifalSalvarResponse } from '@shared/services/responses/motor-fiscal/difal-salvar.response';
import { Component, OnInit } from '@angular/core';
import { listarFormulasContexto } from '@shared/components/formula/utils/formula-contexto.util';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NgxCurrencyDirective } from 'ngx-currency';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { DifalService } from '@shared/services/apis/motor-fiscal/difal.service';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { UnidadeFederativaService } from '@shared/services/apis/usuario/unidade-federativa.service';
import { AtualizarDifalRequest } from '@shared/services/requests/motor-fiscal/atualizar-difal.request';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoGrupoStruct } from '@shared/services/structs/formula/formula-catalogo-grupo.struct';
import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { modeloAtivo, rotuloModeloFormula } from '@shared/components/formula/utils/formula-modelo.util';
import { FormulaSimulacaoDialogComponent, FormulaSimulacaoDialogData } from '@shared/components/formula/formula-simulacao-dialog/formula-simulacao-dialog.component';
import { ApiMotorCalculoEnum } from '@shared/enums/formula/api-motor-calculo.enum';

const CHAVE_VARIAVEL_BASE_CALCULO_DIFAL = 'nfe.infnfe.det.imposto.icmsufdest.vbcufdest';

@Component({
    selector: 'app-tabela-difal-cadastro',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        HeaderComponent,
        FooterActionComponent,
        AtivacaoSectionCardComponent,
    ],
    templateUrl: './tabela-difal-cadastro.component.html',
    styleUrl: './tabela-difal-cadastro.component.css',
})
export class TabelaDifalCadastroComponent implements OnInit {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;

    public form!: FormGroup;
    public idDifal!: number;
    public isLoading = false;
    public isSaving = false;

    public ufLabel = '';
    public formulasDifal: FormulaCatalogoItemStruct[] = [];

    public readonly percentMask = this.maskService.getPercentMask();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private difalService: DifalService,
        private formulaService: FormulaCatalogoService,
        private unidadeFederativaService: UnidadeFederativaService,
        private maskService: MaskService,
        private dialog: MatDialog,
        private alertService: AlertService,
    ) { }

    ngOnInit(): void {
        this.idDifal = +this.route.snapshot.paramMap.get('id')!;

        this.form = this.fb.group({
            pIcmsUfDest: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
            pIcmsInter: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
            pFcpUfDest: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
            isReducaoBc: [false],
            pRedBcDifal: [{ value: null, disabled: true }],
            _estadosFormulas: [[]],
            // Rascunhos de ciclo do DIFAL configurados no modal do salvar (um por ciclo); vão em ciclosEmpresa.
            _ciclosEmpresa: [[] as FormulaCicloEmpresaRascunho[]],
        });

        this.form.get('isReducaoBc')!.valueChanges.subscribe((ativo: boolean) => this.aplicarReducao(!!ativo));

        this.carregar();
    }

    private carregar(): void {
        this.isLoading = true;
        forkJoin({
            catalogo: this.formulaService.getContexto('difal'),
            difal: this.difalService.getCompleto(this.idDifal),
            ufs: this.unidadeFederativaService.getUnidadesFederativas(),
        }).subscribe({
            next: ({ catalogo, difal, ufs }) => {
                this.isLoading = false;
                if (difal.isError) {
                    this.alertService.show('Erro', difal.errorDescription || 'Configuração DIFAL não encontrada.', AlertType.error);
                    this.voltarParaLista();
                    return;
                }

                this.formulasDifal = this.obterFormulasDifal(catalogo.grupos ?? []);

                const uf = ufs.find(u => u.idUnidadeFederativa === difal.idUnidadeFederativa);
                this.ufLabel = uf ? `${uf.id} - ${uf.descricao}` : difal.idUnidadeFederativa.toString();

                this.form.patchValue({
                    pIcmsUfDest: difal.pIcmsUfDest,
                    pIcmsInter: difal.pIcmsInter,
                    pFcpUfDest: difal.pFcpUfDest,
                    isReducaoBc: difal.isReducaoBc,
                    pRedBcDifal: difal.pRedBcDifal,
                });
                this.form.get('_estadosFormulas')?.setValue(difal.formulas ?? []);
                this.aplicarReducao(difal.isReducaoBc);
            },
            error: err => {
                this.isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar a configuração de DIFAL.', AlertType.error);
            },
        });
    }

    private obterFormulasDifal(grupos: FormulaCatalogoGrupoStruct[]): FormulaCatalogoItemStruct[] {
        return grupos.flatMap(listarFormulasContexto);
    }

    private aplicarReducao(ativo: boolean): void {
        const campo = this.form.get('pRedBcDifal') as FormControl;
        if (ativo) {
            campo.addValidators([Validators.required, Validators.min(0), Validators.max(100)]);
            campo.enable({ emitEvent: false });
        } else {
            campo.clearValidators();
            campo.setValue(null, { emitEvent: false });
            campo.disable({ emitEvent: false });
        }
        campo.updateValueAndValidity({ emitEvent: false });
    }

    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.form.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get reducaoBcControl(): FormControl {
        return this.form.get('isReducaoBc') as FormControl;
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    public get personalizarBloqueado(): boolean {
        return this.isLoading || this.formulasDifal.length === 0;
    }

    public get personalizarTooltip(): string {
        return this.personalizarBloqueado ? 'A personalização de fórmulas está indisponível no momento.' : '';
    }

    public get origemFormula(): string {
        const formulaBase = this.formulasDifal.find(
            formula => formula.variavelPrincipal?.chave === CHAVE_VARIAVEL_BASE_CALCULO_DIFAL,
        );
        if (!formulaBase) return 'Modelo indisponível';

        const estado = this.estadosFormulasMemoria.find(item => item.chaveFormula === formulaBase.chaveFormula);
        if (estado?.tipo === TipoFormulaEnum.Personalizada) return 'Personalizada';

        const modelo = estado?.tipo === TipoFormulaEnum.ModeloAlternativo
            ? formulaBase.modelos.find(item => item.chaveFormulaModelo === estado.chaveFormulaModelo)
            : modeloAtivo(formulaBase);

        return modelo ? rotuloModeloFormula(formulaBase, modelo) : 'Modelo indisponível';
    }

    public classeOrigemFormula(): string {
        const formulaBase = this.formulasDifal.find(
            formula => formula.variavelPrincipal?.chave === CHAVE_VARIAVEL_BASE_CALCULO_DIFAL,
        );
        const estado = formulaBase
            ? this.estadosFormulasMemoria.find(item => item.chaveFormula === formulaBase.chaveFormula)
            : null;
        if (estado?.tipo === TipoFormulaEnum.Personalizada) return 'badge-vinho';
        if (estado?.tipo === TipoFormulaEnum.ModeloAlternativo) return 'badge-roxo';
        return 'badge-teal';
    }

    public abrirSimulacao(): void {
        const isReducaoBc = !!this.form.get('isReducaoBc')?.value;
        const data: FormulaSimulacaoDialogData = {
            titulo: 'Simulação do cálculo do DIFAL',
            subtitulo: this.ufLabel,
            api: ApiMotorCalculoEnum.MotorFiscal,
            contextoRaiz: 'difal',
            formulasCatalogo: this.formulasDifal,
            estados: this.mapFormulaItems(this.estadosFormulasMemoria),
            ciclosEmpresa: coletarCiclosEmpresa(this.form),
            // Alíquotas e redução vêm da tela; a partilha é 100% para o destino desde 2019.
            entradasIniciais: [
                { chaveVariavel: 'nfe.infnfe.det.imposto.icmsufdest.picmsufdest', valor: this.form.get('pIcmsUfDest')?.value ?? 0 },
                { chaveVariavel: 'nfe.infnfe.det.imposto.icmsufdest.picmsinter', valor: this.form.get('pIcmsInter')?.value ?? 0 },
                { chaveVariavel: 'nfe.infnfe.det.imposto.icmsufdest.pfcpufdest', valor: this.form.get('pFcpUfDest')?.value ?? 0 },
                { chaveVariavel: 'nfe.infnfe.det.imposto.icmsufdest.picmsinterpart', valor: 100 },
                { chaveVariavel: 'difal.p_red_bc_difal', valor: isReducaoBc ? this.form.get('pRedBcDifal')?.value ?? 0 : 0 },
            ],
        };

        this.dialog.open(FormulaSimulacaoDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });
    }

    public abrirEditorFormulas(): void {
        const data: FormulaEditorDialogData = {
            titulo: ['DIFAL', this.ufLabel].filter(Boolean).join(' - '),
            formulas: this.formulasDifal,
            estadosIniciais: this.estadosFormulasMemoria as any[],
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'difal',
            validarPersonalizacoes: request => this.formulaService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.form.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }

    private mapFormulaItems(estados: FormulaItemStruct[]): FormulaItemStruct[] {
        return estados
            .filter(e => e.tipo !== TipoFormulaEnum.ModeloPadrao)
            .map(e => ({
                chaveFormula: e.chaveFormula,
                idFormulaPersonalizada: e.idFormulaPersonalizada ?? null,
                tipo: e.tipo,
                chaveFormulaModelo: e.chaveFormulaModelo ?? null,
                expressao: e.expressao ?? null,
                tipoArredondamento: e.tipoArredondamento ?? null,
                casasDecimais: e.casasDecimais ?? null,
                dataVigenciaInicio: e.dataVigenciaInicio ?? null,
                dataVigenciaFim: e.dataVigenciaFim ?? null,
            }));
    }

    public salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.show('Atenção', 'Preencha os campos obrigatórios antes de salvar.', AlertType.warning);
            return;
        }

        this.isSaving = true;
        const v = this.form.getRawValue();

        const request: AtualizarDifalRequest = {
            pIcmsUfDest: v.pIcmsUfDest ?? 0,
            pIcmsInter: v.pIcmsInter ?? 0,
            pFcpUfDest: v.pFcpUfDest ?? 0,
            isReducaoBc: v.isReducaoBc,
            pRedBcDifal: v.isReducaoBc ? (v.pRedBcDifal ?? null) : null,
            formulas: this.mapFormulaItems(v._estadosFormulas ?? []),
            ciclosEmpresa: coletarCiclosEmpresa(this.form),
        };

        this.difalService.atualizarCompleto(this.idDifal, request).subscribe({
            next: response => {
                this.isSaving = false;
                if (response.isError) {
                    this.tratarErroSalvar(response);
                    return;
                }
                this.alertService.show('Sucesso', 'Configuração DIFAL salva com sucesso.', AlertType.success);
                this.voltarParaLista();
            },
            error: err => {
                this.isSaving = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao salvar a configuração de DIFAL.', AlertType.error);
            },
        });
    }

    /**
     * Erro do salvar. Referência circular ou ciclo alterado abre o modal de ciclo com o ciclo devolvido pela API;
     * ao confirmar, o rascunho é acrescentado a `_ciclosEmpresa` e o salvar é repetido, um ciclo por vez. Cancelar preserva o
     * formulário e avisa com texto fixo. Os demais códigos (ciclo ambíguo, pontos de decisão inválidos etc.) só avisam.
     */
    private tratarErroSalvar(response: DifalSalvarResponse): void {
        const erros = response.erros ?? [];
        const erroCiclo = erros.find(item =>
            (item.codigo === FormulaErrorCodeEnum.ReferenciaCircular || item.codigo === FormulaErrorCodeEnum.CicloAlterado
                || item.codigo === FormulaErrorCodeEnum.CicloPontosDecisaoInvalidos)
            && item.ciclo?.membros?.length);
        if (!erroCiclo?.ciclo) {
            const mensagem = erros.map(item => item.mensagem).filter(Boolean).join('\n')
                || response.errorDescription || 'Erro ao salvar a configuração de DIFAL.';
            this.alertService.show('Erro', mensagem, AlertType.error);
            return;
        }

        const data: FormulaCicloDialogData = {
            ciclo: erroCiclo.ciclo,
            titulo: 'Referência circular entre fórmulas',
            tipoEntidadeEdicao: TipoEntidadeFormulaEnum.Difal,
            idEntidadeEdicao: this.idDifal,
        };
        this.dialog.open(FormulaCicloDialogComponent, { width: '760px', maxWidth: '96vw', maxHeight: '92vh', data })
            .afterClosed().subscribe((rascunho: FormulaCicloDialogResult | null) => {
                if (!rascunho) {
                    this.alertService.show('Atenção', 'O salvamento foi cancelado: escolha por onde o cálculo começa para salvar.', AlertType.warning);
                    return;
                }
                registrarCicloEmpresa(this.form, rascunho);
                this.salvar();
            });
    }

    public cancelar(): void {
        this.voltarParaLista();
    }

    private voltarParaLista(): void {
        this.router.navigate(['/configuracoes/notas-fiscais/tabela-difal']);
    }
}
